import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useAppSelector, useAppDispatch } from '@store/hooks'
import {
  fetchTreatmentPlanbyPatientId,
  updateTreatmentPlan,
} from '@store/actions'
import {
  Accordion,
  AccordionItem,
  ActionButton,
  Button,
  ChartingTabs,
  DataTable,
  ScreenLoader,
  type DataTableColumn,
  type DataTableRow,
} from '@enamelai/design-system'
import { Icon as NotesIcon } from '@enamelai/design-system/icons/Notes'
import { Icon as ViewIcon } from '@enamelai/design-system/icons/View'
import { Icon as HideIcon } from '@enamelai/design-system/icons/Hide'
import TreatmentPlanToothChart from './TreatmentPlanToothChart/TreatmentPlanToothChart.component'
import TxPlanStatusDropdown from './TxPlanStatusDropdown'
import { getFullName } from '@utils/fullname.util'
import {
  ITreatmentPlan,
  TxPlanStatus,
  IPhaseItems,
  IPhases,
  TX_PLAN_STATUS,
} from '@dataTypes/treatmentPlan.types'
import { formatNumber } from '@utils/numberFormatter.util'
import {
  getTSFromToothSurfaces,
  parseSurfaceString,
} from '@utils/treatment-plan.utils'
import { formatToothSurfaceEntriesForDisplay } from '@utils/procedure-surface-selection.utils'
import EmptyTreatmentPlan from './EmptyState/EmptyTreatmentPlan.component'
import { Patient } from '@dataTypes/PatientInfo.types'
import PhaseActionMenuButton, {
  type PhaseMenuItem,
} from './PhaseActionMenuButton/PhaseActionMenuButton.component'
import './TreatmentPlanMain.css'
import {
  getTreatmentPlanChartAccordionClassName,
  TP_CHART_PANE_MIN_PX,
  TP_CHART_VERTICAL_HANDLE_PX,
  useTreatmentPlanChartVerticalSplit,
} from './useTreatmentPlanChartVerticalSplit.hook'
import { useClinicChartingPreferences } from '@hooks/useClinicChartingPreferences'
import { TX_PLAN_INSURANCE_ESTIMATE_COLUMN_FIELDS } from '@utils/tx-plan-procedure-grid-columns.util'

type HistoryPhaseItemRow = DataTableRow & {
  id: string
  code: string
  description: string
  provider: string
  surface: string
  charges: string
  allowed_amt: string
  write_off: string
  adjustments: string
  insurance: string
  outstanding: string
}

const formatHistoryPhaseSurface = (item: IPhaseItems): string => {
  const toothSurfaceEntries = getTSFromToothSurfaces(item.tooth_surfaces)
  const surfaceEntries = parseSurfaceString(item.surface)
  const parsedEntries =
    toothSurfaceEntries.length > 0 ? toothSurfaceEntries : surfaceEntries
  if (parsedEntries.length === 0) {
    return '-'
  }
  return formatToothSurfaceEntriesForDisplay(parsedEntries)
}

const HISTORY_PHASE_COLUMNS: DataTableColumn<HistoryPhaseItemRow>[] = [
  { id: 'code', label: 'Code' },
  { id: 'description', label: 'Description' },
  { id: 'provider', label: 'Provider' },
  { id: 'surface', label: 'T/S' },
  { id: 'charges', label: 'Clinic fee' },
  { id: 'allowed_amt', label: 'Allowed amt.' },
  { id: 'write_off', label: 'Write-off' },
  { id: 'adjustments', label: 'Discount' },
  { id: 'insurance', label: 'Est. ins. pay' },
  { id: 'outstanding', label: 'Est. pt. res.' },
]

interface TreatmentPlanHistoryProps {
  onViewNote?: (id: string) => void
  onGoToPlans?: () => void
  patient?: Partial<Patient> | null
}

const TreatmentPlanHistory: React.FC<TreatmentPlanHistoryProps> = ({
  onViewNote,
  onGoToPlans,
  patient: patientProp,
}): JSX.Element => {
  const selectedPatientFromRedux = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const allTreatmentPlans = useAppSelector(
    (state) => state.charting.treatmentPlans,
  )
  const chartingLoading = useAppSelector((state) => state.charting.loading)
  const { showInsuranceEstimate } = useClinicChartingPreferences()
  const providerList = useAppSelector((state) => state.provider.data)
  const visibleHistoryPhaseColumns = useMemo(
    (): DataTableColumn<HistoryPhaseItemRow>[] =>
      showInsuranceEstimate
        ? HISTORY_PHASE_COLUMNS
        : HISTORY_PHASE_COLUMNS.filter(
            (column) =>
              !TX_PLAN_INSURANCE_ESTIMATE_COLUMN_FIELDS.has(column.id),
          ),
    [showInsuranceEstimate],
  )
  const dispatch = useAppDispatch()

  // Use patient prop if provided, otherwise fall back to Redux
  const selectedPatient = patientProp || selectedPatientFromRedux

  // Fetch treatment plans when patient prop is provided
  useEffect(() => {
    if (patientProp?.patient_id) {
      dispatch(fetchTreatmentPlanbyPatientId(patientProp.patient_id))
    }
  }, [patientProp?.patient_id, dispatch])

  // Filter treatment plans by patient if patient prop is provided
  const treatmentPlans = useMemo(() => {
    if (patientProp?.patient_id) {
      return allTreatmentPlans.filter(
        (plan) => plan.patient_id === patientProp.patient_id,
      )
    }
    return allTreatmentPlans
  }, [allTreatmentPlans, patientProp])

  const [activeTabKey, setActiveTabKey] = useState<string>('')
  const [chartVisible, setChartVisible] = useState<boolean>(true)
  const mainContainerRef = useRef<HTMLDivElement>(null)
  const [phaseVisibility, setPhaseVisibility] = useState<
    Record<string, boolean>
  >({})

  const completedTreatmentPlans = useMemo(() => {
    return treatmentPlans.filter((plan) => {
      if (!plan.phases || plan.phases.length === 0) return false
      return plan.phases.every(
        (phase) => phase.status === TX_PLAN_STATUS.COMPLETED,
      )
    })
  }, [treatmentPlans])

  const isEmptyState = completedTreatmentPlans.length === 0
  const {
    chartPaneHeightPx,
    chartPaneRef,
    onHandleMouseDown: onChartVerticalHandleMouseDown,
  } = useTreatmentPlanChartVerticalSplit({
    isSplitActive: chartVisible,
    containerRef: mainContainerRef,
    isEmptyState,
  })

  // Set active tab to first completed plan when available
  useEffect(() => {
    if (completedTreatmentPlans.length > 0 && !activeTabKey) {
      setActiveTabKey(completedTreatmentPlans[0].tp_id)
    } else if (completedTreatmentPlans.length === 0) {
      setActiveTabKey('')
    }
  }, [completedTreatmentPlans, activeTabKey])

  // Toggle phase visibility in chart
  const togglePhaseVisibility = useCallback((phaseId: string): void => {
    setPhaseVisibility((prev) => ({
      ...prev,
      [phaseId]: !(prev[phaseId] ?? true),
    }))
  }, [])

  // Get phase visibility state (defaults to true)
  const getPhaseVisibility = (phaseId: string): boolean => {
    return phaseVisibility[phaseId] ?? true
  }

  // Handle marking phase as incomplete (changes status to Accepted)
  const handleMarkPhaseAsIncomplete = useCallback(
    async (phaseId: string, planId: string): Promise<void> => {
      const plan = completedTreatmentPlans.find((p) => p.tp_id === planId)
      if (!plan || !plan.phases) return

      const updatedPhases = plan.phases.map((phase) =>
        phase.phase_id === phaseId
          ? { ...phase, status: TX_PLAN_STATUS.ACCEPTED }
          : phase,
      )

      await dispatch(
        updateTreatmentPlan({
          treatmentPlanId: planId,
          treatmentPlan: { ...plan, phases: updatedPhases },
        }),
      )
    },
    [completedTreatmentPlans, dispatch],
  )

  // Handle menu selection from PhaseActionMenuButton
  const handleMenuSelect = useCallback(
    (
      itemId: string,
      tabKey: string,
      originalIdx: number,
      phase: IPhases,
    ): void => {
      if (itemId === 'mark-as-incomplete') {
        const phaseId = phase.phase_id || `phase-${originalIdx}`
        handleMarkPhaseAsIncomplete(phaseId, tabKey)
      }
    },
    [handleMarkPhaseAsIncomplete],
  )

  const historyMenuItems: PhaseMenuItem[] = [
    {
      id: 'mark-as-incomplete',
      label: 'Mark as incomplete',
    },
  ]

  const handleTabSelect = (key: string): void => {
    setActiveTabKey(key)
  }

  // Get chart header name
  const chartHeaderName = useMemo(() => {
    if (!activeTabKey) return 'Current chart'
    const plan = completedTreatmentPlans.find((p) => p.tp_id === activeTabKey)
    if (plan) {
      return `${plan.tp_heading || 'Treatment plan'} - Chart`
    }
    return 'Current chart'
  }, [completedTreatmentPlans, activeTabKey])

  // Memoized form state phases for chart (read-only, from Redux)
  const memoizedFormStatePhases = useMemo(() => {
    if (!activeTabKey) return null
    const plan = completedTreatmentPlans.find((p) => p.tp_id === activeTabKey)
    return plan?.phases || null
  }, [completedTreatmentPlans, activeTabKey])

  const renderTreatmentPlanForm = (
    tabKey: string,
    plan: ITreatmentPlan,
  ): JSX.Element => {
    const phases = plan.phases || []

    const totals: {
      charges: number
      insurance: number
      adjustments: number
      outstanding: number
    } = phases.reduce(
      (sum, phase) => {
        for (const item of phase.items) {
          sum.charges += item.charges
          sum.insurance += item.insurance
          sum.adjustments += item.adjustments
          sum.outstanding += item.outstanding
        }
        return sum
      },
      { charges: 0, insurance: 0, adjustments: 0, outstanding: 0 },
    )

    return (
      <div className="treatment-plan-form-container">
        <div className="create-tx-plan-container">
          {/* Phases */}
          <div id={`tx-plan-phase-accordion-${tabKey}`}>
            <Accordion
              className="tx-plan-phase-accordion"
              allowMultiple
              defaultOpenIds={[]}
            >
              {phases
                .map((phase, originalIdx) => ({ phase, originalIdx }))
                .filter(({ phase }) => !phase.is_hidden)
                .map(({ phase, originalIdx }, phaseIdx) => {
                  const isFirstTab =
                    completedTreatmentPlans.length > 0
                      ? tabKey === completedTreatmentPlans[0]?.tp_id
                      : false
                  const isFirstPhase = phaseIdx === 0
                  const isFirstPhaseInFirstTab = isFirstTab && isFirstPhase
                  const phaseId = phase.phase_id || `phase-${originalIdx}`
                  const visibleItems = phase.items.filter(
                    (item) => !item.is_hidden,
                  )
                  const historyPhaseRows: HistoryPhaseItemRow[] =
                    visibleItems.map((item, itemIdx) => {
                      const provider = providerList.find(
                        (p) => p.provider_id === item.provider_id,
                      )
                      return {
                        id: item.item_id || `${phaseId}-${itemIdx}`,
                        code:
                          item.procedure_settings?.procedure?.procedure_code ??
                          '',
                        description:
                          item.procedure_settings?.procedure?.description ?? '',
                        provider: provider
                          ? `Dr. ${getFullName(provider)}`
                          : '-',
                        surface: formatHistoryPhaseSurface(item),
                        charges: formatNumber(item.charges ?? 0, true),
                        allowed_amt: formatNumber(
                          item.allowed_amount ?? 0,
                          true,
                        ),
                        write_off: formatNumber(item.write_off ?? 0, true),
                        adjustments: formatNumber(item.adjustments ?? 0, true),
                        insurance: formatNumber(item.insurance ?? 0, true),
                        outstanding: formatNumber(item.outstanding ?? 0, true),
                      }
                    })
                  return (
                    <div
                      key={phaseId}
                      className={`tx-plan-phase-row${
                        isFirstPhaseInFirstTab
                          ? ' tx-plan-phase-row--first'
                          : ''
                      }`}
                    >
                      <div
                        className={`tx-plan-phase-header tx-plan-phase-header-overlay border-0${
                          isFirstPhaseInFirstTab
                            ? ' first-phase-header-no-radius'
                            : ''
                        }`}
                      >
                        <div className="w-100">
                          <div className="tx-plan-phase-header-row">
                            <div className="d-flex flex-row align-items-center gap-2 flex-nowrap min-w-0">
                              <div className="create-tx-plan-set-date d-flex flex-row align-items-center gap-2 flex-nowrap min-w-0">
                                <div className="create-tx-plan-phase-name">
                                  {phase.phase_name}
                                </div>
                                {phase.scheduled_date && (
                                  <span>
                                    Scheduled{' '}
                                    {new Date(
                                      phase.scheduled_date,
                                    ).toLocaleDateString()}
                                  </span>
                                )}
                                {!phase.scheduled_date && (
                                  <span>Unscheduled</span>
                                )}
                              </div>
                            </div>
                            <div className="d-flex flex-row gap-2 justify-content-end">
                              <div
                                className="d-flex flex-row gap-1 align-items-center tx-plan-phase-header-actions"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ActionButton
                                  className="phase-visibility-button"
                                  size="sm"
                                  label={
                                    getPhaseVisibility(
                                      phase.phase_id || `phase-${originalIdx}`,
                                    )
                                      ? 'Hide phase conditions in chart'
                                      : 'Show phase conditions in chart'
                                  }
                                  icon={
                                    getPhaseVisibility(
                                      phase.phase_id || `phase-${originalIdx}`,
                                    ) ? (
                                      <ViewIcon width={16} height={16} />
                                    ) : (
                                      <HideIcon width={16} height={16} />
                                    )
                                  }
                                  onClick={() => {
                                    togglePhaseVisibility(
                                      phase.phase_id || `phase-${originalIdx}`,
                                    )
                                  }}
                                />
                                <TxPlanStatusDropdown
                                  value={
                                    (phase.status ||
                                      TX_PLAN_STATUS.PROPOSED) as TxPlanStatus
                                  }
                                  readOnly
                                />
                                <PhaseActionMenuButton
                                  tabKey={tabKey}
                                  originalIdx={originalIdx}
                                  phase={phase}
                                  plan={plan}
                                  isEditMode={false}
                                  onMenuSelect={handleMenuSelect}
                                  customMenuItems={historyMenuItems}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <AccordionItem
                        id={phaseId}
                        title={phase.phase_name}
                        className="tx-plan-phase-accordion-item"
                      >
                        <div className="create-tx-plan-phase-table">
                          {historyPhaseRows.length > 0 && (
                            <DataTable<HistoryPhaseItemRow>
                              columns={visibleHistoryPhaseColumns}
                              rows={historyPhaseRows}
                              showActions={false}
                            />
                          )}
                        </div>
                      </AccordionItem>
                    </div>
                  )
                })}
            </Accordion>
          </div>
        </div>

        {/* Totals */}
        <div className="create-tx-plan-summary">
          <div className="ctps-title">Plan pricing summary</div>
          <div className="create-tx-plan-summary-split">
            <div>
              <div className="tx-label">Total charges:</div>
              {showInsuranceEstimate && (
                <div className="tx-label">Insurance covered:</div>
              )}
              <div className="tx-label">Adjustments:</div>
            </div>
            <div className="create-tx-plan-summary-amounts">
              <div className="tx-amount">
                {formatNumber(totals.charges, true)}
              </div>
              {showInsuranceEstimate && (
                <div className="tx-amount">
                  {formatNumber(totals.insurance, true)}
                </div>
              )}
              <div className="tx-amount">
                {formatNumber(totals.adjustments, true)}
              </div>
            </div>
          </div>
          {showInsuranceEstimate && (
            <div className="create-tx-plan-summary-patient">
              <div className="tx-label">Adjusted amount:</div>
              <div className="tx-amount">
                {formatNumber(totals.outstanding, true)}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={mainContainerRef}
      className={`treatment-plan-main-container ${chartingLoading ? 'overlay-active' : ''}`}
    >
      {chartingLoading && (
        <div className="treatment-plan-overlay">
          <div className="treatment-plan-loading">
            <ScreenLoader
              open
              size="md"
              inline
              aria-label="Loading patient treatment plans"
            />
            <p className="treatment-plan-loading-text">
              Loading patient treatment plans...
            </p>
          </div>
        </div>
      )}

      {!selectedPatient ? (
        <EmptyTreatmentPlan description="Select a patient to view their treatment plans." />
      ) : (
        <>
          <div
            className={`treatment-plan-chart-accordion-sticky tx-plan-history-chart-row ${getTreatmentPlanChartAccordionClassName(chartVisible)}`}
          >
            <Accordion
              className="treatment-plan-chart-accordion"
              openIds={chartVisible ? ['chart'] : []}
              onOpenChange={(): void => undefined}
            >
              <AccordionItem
                id="chart"
                title={chartHeaderName}
                className="treatment-plan-chart-accordion-item"
              >
                <div
                  ref={chartPaneRef}
                  className={`treatment-plan-chart-body-content${
                    chartPaneHeightPx !== null
                      ? ' treatment-plan-chart-body-content--sized'
                      : ''
                  }`}
                  style={
                    chartPaneHeightPx !== null
                      ? ({
                          '--tp-chart-pane-height': `${chartPaneHeightPx}px`,
                          height: chartPaneHeightPx,
                          maxHeight: chartPaneHeightPx,
                        } as React.CSSProperties)
                      : undefined
                  }
                >
                  <TreatmentPlanToothChart
                    activeTreatmentPlanId={
                      activeTabKey && activeTabKey !== 'create-new'
                        ? activeTabKey
                        : null
                    }
                    phaseVisibility={phaseVisibility}
                    formStatePhases={memoizedFormStatePhases}
                    fillPane={chartPaneHeightPx !== null}
                  />
                </div>
              </AccordionItem>
            </Accordion>
            <div className="treatment-plan-chart-header treatment-plan-chart-header-overlay">
              <div className="treatment-plan-chart-header-content">
                <div className="d-flex align-items-center gap-2">
                  <span className="treatment-plan-chart-header-title">
                    {chartHeaderName}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <span className="small">Dental chart</span>
                    <i
                      className={`icon-wrap ${chartVisible ? 'mgc_eye_2_fill' : 'mgc_eye_close_line'}`}
                      style={{ cursor: 'pointer', fontSize: '18px' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setChartVisible(!chartVisible)
                      }}
                    ></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {chartVisible ? (
            <div
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize dental chart"
              aria-valuenow={chartPaneHeightPx ?? TP_CHART_PANE_MIN_PX}
              aria-valuemin={TP_CHART_PANE_MIN_PX}
              tabIndex={0}
              className="treatment-plan-chart-vertical-handle"
              onMouseDown={onChartVerticalHandleMouseDown}
              style={{ flex: `0 0 ${TP_CHART_VERTICAL_HANDLE_PX}px` }}
            >
              <i
                className="mgc_unfold_vertical_line treatment-plan-chart-vertical-handle__icon"
                aria-hidden
              />
            </div>
          ) : null}
          {completedTreatmentPlans.length === 0 ? (
            <div className="treatment-plan-empty-scroll">
              <EmptyTreatmentPlan
                title="No completed plan"
                description="Completed treatment plans will be added here."
                actionLabel="Go to plan"
                onCreateNewPlan={onGoToPlans}
              />
            </div>
          ) : (
            <div className="treatment-plan-tabs-container">
              <div className="treatment-plan-tabs-header">
                <ChartingTabs
                  className="treatment-plan-tabs"
                  items={completedTreatmentPlans.map((plan) => ({
                    id: plan.tp_id,
                    label: plan.tp_heading || 'Untitled Plan',
                  }))}
                  value={activeTabKey}
                  onChange={handleTabSelect}
                />
                {activeTabKey &&
                  completedTreatmentPlans.find(
                    (p) => p.tp_id === activeTabKey,
                  ) &&
                  onViewNote && (
                    <div className="treatment-plan-tab-actions">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="charting-toolbar-icon-button"
                        aria-label="Add note"
                        title="Add note"
                        leftIcon={
                          <NotesIcon width={16} height={16} aria-hidden />
                        }
                        onClick={() => {
                          const plan = completedTreatmentPlans.find(
                            (p) => p.tp_id === activeTabKey,
                          )
                          if (plan) {
                            onViewNote(plan.tp_id)
                          }
                        }}
                      />
                    </div>
                  )}
              </div>
              {completedTreatmentPlans.map((plan) =>
                activeTabKey === plan.tp_id
                  ? renderTreatmentPlanForm(plan.tp_id, plan)
                  : null,
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default TreatmentPlanHistory
